/**
 * Trip Service - Frontend service for trip operations
 */

import * as tripRepository from "../repositories/tripRepository";
import { logError, logInfo } from "../../../shared/utils/logger.js";

export const getTrips = async (filters = {}, page = 1, limit = 20) => {
  try {
    const response = await tripRepository.fetchTrips(filters, page, limit);
    if (!response.success)
      throw new Error(response.error || "Failed to fetch trips");
    return response;
  } catch (error) {
    logError("Failed to fetch trips", {
      filters,
      error: error.message,
    });
    throw error;
  }
};

export const getTrip = async (tripId) => {
  try {
    const response = await tripRepository.fetchTripById(tripId);
    if (!response.success)
      throw new Error(response.error || "Failed to fetch trip");
    return response.data;
  } catch (error) {
    logError("Failed to fetch trip", {
      tripId,
      error: error.message,
    });
    throw error;
  }
};

export const createTrip = async (tripData) => {
  try {
    const response = await tripRepository.createTrip(tripData);
    if (!response.success)
      throw new Error(response.error || "Failed to create trip");
    logInfo("Trip created successfully", { tripId: response.data?.id });
    return response;
  } catch (error) {
    logError("Failed to create trip", {
      tripData,
      error: error.message,
    });
    throw error;
  }
};

export const updateTrip = async (tripId, updateData) => {
  try {
    const response = await tripRepository.updateTrip(tripId, updateData);
    if (!response.success)
      throw new Error(response.error || "Failed to update trip");
    return response;
  } catch (error) {
    logError("Failed to update trip", {
      tripId,
      updateData,
      error: error.message,
    });
    throw error;
  }
};

export const updateTripStatus = async (tripId, status) => {
  try {
    const response = await tripRepository.updateTripStatus(tripId, status);
    if (!response.success)
      throw new Error(response.error || "Failed to update trip status");
    return response.data;
  } catch (error) {
    logError("Failed to update trip status", {
      tripId,
      status,
      error: error.message,
    });
    throw error;
  }
};

export const addOrderToTrip = async (tripId, orderId) => {
  try {
    const response = await tripRepository.addOrderToTrip(tripId, orderId);
    if (!response.success)
      throw new Error(response.error || "Failed to add order to trip");
    return response.data;
  } catch (error) {
    logError("Failed to add order to trip", {
      tripId,
      orderId,
      error: error.message,
    });
    throw error;
  }
};

export const removeOrderFromTrip = async (tripId, orderId) => {
  try {
    const response = await tripRepository.removeOrderFromTrip(tripId, orderId);
    if (!response.success)
      throw new Error(response.error || "Failed to remove order from trip");
    return response.data;
  } catch (error) {
    logError("Failed to remove order from trip", {
      tripId,
      orderId,
      error: error.message,
    });
    throw error;
  }
};

export const deleteTrip = async (tripId) => {
  try {
    const response = await tripRepository.deleteTrip(tripId);
    if (!response.success)
      throw new Error(response.error || "Failed to delete trip");
    return response;
  } catch (error) {
    logError("Failed to delete trip", {
      tripId,
      error: error.message,
    });
    throw error;
  }
};

export const getTripRisks = async (tripId) => {
  try {
    const response = await tripRepository.fetchTripRisks(tripId);
    if (!response.success)
      throw new Error(response.error || "Failed to fetch trip risks");
    return response.data;
  } catch (error) {
    logError("Failed to fetch trip risks", {
      tripId,
      error: error.message,
    });
    throw error;
  }
};

export const getTripSummary = async (tripId) => {
  try {
    const response = await tripRepository.fetchTripSummary(tripId);
    if (!response.success)
      throw new Error(response.error || "Failed to fetch trip summary");
    return response.data;
  } catch (error) {
    logError("Failed to fetch trip summary", {
      tripId,
      error: error.message,
    });
    throw error;
  }
};

/**
 * Batch-load trips referenced by orders into an id → trip map.
 * @param {string[]} tripIds
 * @returns {Promise<Record<string, Object>>}
 */
export const loadTripsMap = async (tripIds = []) => {
  const unique = [...new Set(tripIds.filter(Boolean))];
  if (!unique.length) return {};

  const results = await Promise.allSettled(unique.map((id) => getTrip(id)));
  const map = {};
  results.forEach((result, index) => {
    if (result.status === "fulfilled" && result.value) {
      map[unique[index]] = result.value;
    }
  });
  return map;
};

